import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Annotation, EditorState } from '@codemirror/state';
import {
  EditorView,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { HighlightStyle, bracketMatching, syntaxHighlighting, syntaxTree } from '@codemirror/language';
import { json } from '@codemirror/lang-json';
import { lintGutter, setDiagnostics } from '@codemirror/lint';
import { tags } from '@lezer/highlight';

// CodeMirror 6 wrapper for the schema-definition JSON (doc-74). Owns nothing
// but the text surface: the parent computes diagnostics (parse + meta-schema)
// and pushes them in via the imperative handle; both themes come from design
// tokens, so light/dark need no editor-side switching.

const theme = EditorView.theme({
  '&': {
    border: 'var(--stroke-xs) solid var(--field-border)',
    borderRadius: 'var(--corner-radius-sm)',
    backgroundColor: 'var(--field-bg)',
    fontSize: '12px',
    color: 'var(--text-primary)',
  },
  '&.cm-focused': { outline: 'none', boxShadow: 'var(--input-focus)' },
  '.cm-scroller': {
    fontFamily: 'var(--font-family-mono)',
    lineHeight: 1.5,
    minHeight: '420px',
    borderRadius: 'var(--corner-radius-sm)',
  },
  '.cm-content': { caretColor: 'var(--text-primary)' },
  '.cm-cursor': { borderLeftColor: 'var(--text-primary)' },
  '.cm-gutters': {
    backgroundColor: 'var(--surface-secondary)',
    color: 'var(--text-tertiary)',
    border: 'none',
    borderRight: 'var(--stroke-xs) solid var(--field-border)',
  },
  '.cm-activeLine': { backgroundColor: 'var(--surface-hover)' },
  '.cm-activeLineGutter': { backgroundColor: 'var(--surface-hover)' },
  '.cm-selectionBackground, &.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground': {
    backgroundColor: 'var(--surface-hover)',
  },
  // Lint markers — replace the library's hardcoded squiggle images with
  // token-colored underlines so both themes stay coherent.
  '.cm-lintRange-error': {
    backgroundImage: 'none',
    textDecoration: 'underline wavy var(--text-critical) 1px',
    textUnderlineOffset: '3px',
  },
  '.cm-lintRange-warning': {
    backgroundImage: 'none',
    textDecoration: 'underline wavy var(--text-yellow) 1px',
    textUnderlineOffset: '3px',
  },
  '.cm-lint-marker-error': { content: 'none' },
  '.cm-tooltip': {
    backgroundColor: 'var(--surface-panel)',
    color: 'var(--text-primary)',
    border: 'var(--stroke-xs) solid var(--border-primary)',
    borderRadius: 'var(--corner-radius-sm)',
    fontFamily: 'inherit',
    fontSize: '12px',
  },
});

const highlight = HighlightStyle.define([
  { tag: tags.propertyName, color: 'var(--text-primary)', fontWeight: 500 },
  { tag: tags.string, color: 'var(--text-secondary)' },
  { tag: tags.number, color: 'var(--text-yellow)' },
  { tag: [tags.bool, tags.null], color: 'var(--text-yellow)' },
  { tag: tags.punctuation, color: 'var(--text-tertiary)' },
]);

// Marks programmatic rewrites (form edits, AI drafts, reset) so the update
// listener doesn't echo them back to the parent as user edits.
const ExternalRewrite = Annotation.define();

const VALUE_NODES = new Set(['Object', 'Array', 'String', 'Number', 'True', 'False', 'Null']);

function childValues(node) {
  const out = [];
  for (let c = node.firstChild; c; c = c.nextSibling) {
    if (VALUE_NODES.has(c.name)) out.push(c);
  }
  return out;
}

// Resolve a structured path (['fields', 2, 'key']) to the {from,to} of the
// node it names — the property NAME when the last segment is a key (that is
// where the eye should land), the value node otherwise.
function findNodeForPath(state, path) {
  const tree = syntaxTree(state);
  let node = null;
  for (let c = tree.topNode.firstChild; c; c = c.nextSibling) {
    if (VALUE_NODES.has(c.name)) { node = c; break; }
  }
  if (!node) return null;
  let hit = { from: node.from, to: Math.min(node.to, node.from + 1) };
  for (const seg of path) {
    if (!node) return hit;
    if (typeof seg === 'number') {
      if (node.name !== 'Array') return hit;
      const items = childValues(node);
      node = items[seg] || null;
      if (node) hit = { from: node.from, to: Math.min(node.to, node.from + 40) };
    } else {
      if (node.name !== 'Object') return hit;
      let next = null;
      for (let prop = node.firstChild; prop; prop = prop.nextSibling) {
        if (prop.name !== 'Property') continue;
        const nameNode = prop.firstChild;
        if (!nameNode || nameNode.name !== 'PropertyName') continue;
        let name;
        try { name = JSON.parse(state.sliceDoc(nameNode.from, nameNode.to)); } catch (_) { continue; }
        if (name === seg) {
          hit = { from: nameNode.from, to: nameNode.to };
          next = childValues(prop)[0] || null;
          break;
        }
      }
      node = next;
    }
  }
  return hit;
}

const SchemaJsonEditor = forwardRef(function SchemaJsonEditor({ value, onChange, ariaLabel }, ref) {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const initialValue = useRef(value);

  useEffect(() => {
    const view = new EditorView({
      state: EditorState.create({
        doc: initialValue.current || '',
        extensions: [
          lineNumbers(),
          history(),
          highlightActiveLine(),
          highlightActiveLineGutter(),
          bracketMatching(),
          json(),
          syntaxHighlighting(highlight),
          lintGutter(),
          keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
          theme,
          EditorView.lineWrapping,
          EditorView.contentAttributes.of({
            'aria-label': ariaLabel || 'Schema definition JSON',
            spellcheck: 'false',
          }),
          EditorView.updateListener.of((update) => {
            if (!update.docChanged) return;
            if (update.transactions.some((tr) => tr.annotation(ExternalRewrite))) return;
            onChangeRef.current(update.state.doc.toString());
          }),
        ],
      }),
      parent: containerRef.current,
    });
    viewRef.current = view;
    return () => { view.destroy(); viewRef.current = null; };
    // Mounted once per editor instance; ariaLabel is static in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // External rewrites (form edit, AI draft, reset) — never echo the editor's
  // own keystrokes back into it.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (value !== current) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value || '' },
        annotations: ExternalRewrite.of(true),
      });
    }
  }, [value]);

  useImperativeHandle(ref, () => ({
    // issues: [{ path?: (string|number)[], pos?: number, severity, message }]
    applyIssues(issues) {
      const view = viewRef.current;
      if (!view) return;
      const docLength = view.state.doc.length;
      const diagnostics = (issues || []).map((issue) => {
        let from = 0;
        let to = Math.min(1, docLength);
        if (typeof issue.pos === 'number') {
          from = Math.max(0, Math.min(issue.pos, Math.max(0, docLength - 1)));
          to = Math.min(from + 1, docLength);
        } else if (Array.isArray(issue.path)) {
          const loc = findNodeForPath(view.state, issue.path);
          if (loc) { from = loc.from; to = Math.max(loc.to, loc.from + 1); }
        }
        return { from, to: Math.min(to, docLength), severity: issue.severity || 'error', message: issue.message };
      }).filter((d) => d.from <= d.to);
      view.dispatch(setDiagnostics(view.state, diagnostics));
    },
    scrollToIssue(issue) {
      const view = viewRef.current;
      if (!view) return;
      let pos = 0;
      if (typeof issue.pos === 'number') pos = Math.min(issue.pos, view.state.doc.length);
      else if (Array.isArray(issue.path)) pos = findNodeForPath(view.state, issue.path)?.from ?? 0;
      view.dispatch({
        selection: { anchor: pos },
        effects: EditorView.scrollIntoView(pos, { y: 'center' }),
      });
      view.focus();
    },
  }), []);

  return <div ref={containerRef} />;
});

export default SchemaJsonEditor;
